import { Typography } from 'antd';

import type { ComponentProps } from 'react';

/**
 * Semantic text primitives — the visual hierarchy convention for all screens:
 *
 * - `PageTitle`: one per page (h1).
 * - `SectionHeading` / `SubsectionHeading`: card & section titles (h3 / h4).
 * - `LabelTitle`: bold inline field/section labels.
 * - `Label`: quiet uppercase group label (filter sidebars, stat cards, tables).
 * - `Meta`: secondary metadata lines (dates, locations, counts).
 * - `Caption`: raw small-text paragraph when neither `Label` nor `Meta` fits.
 * - `DataValue`: prominent stat values — bold/large is reserved for data.
 *
 * Prefer these over raw `Typography.Title level={n}` / ad-hoc `strong` so
 * emphasis stays consistent and scarce.
 */

type TitleProps = Omit<ComponentProps<typeof Typography.Title>, 'level'>;
type TextProps = ComponentProps<typeof Typography.Text>;
type CaptionProps = ComponentProps<typeof Typography.Paragraph>;

export const Caption = ({ styles, ...props }: CaptionProps) => {
  const callerStyles = typeof styles === 'function' ? undefined : styles;

  return (
    <Typography.Paragraph
      {...props}
      styles={{
        ...callerStyles,
        root: {
          fontSize: 'var(--ant-font-size-sm)',
          marginBottom: 0,
          ...callerStyles?.root,
        },
      }}
    />
  );
};

export const PageTitle = (props: TitleProps) => (
  <Typography.Title level={1} {...props} />
);

export const SectionHeading = (props: TitleProps) => (
  <Typography.Title level={3} {...props} />
);

export const SubsectionHeading = (props: TitleProps) => (
  <Typography.Title level={4} {...props} />
);

export const LabelTitle = (props: TextProps) => (
  <Typography.Text strong {...props} />
);

export const Label = (props: CaptionProps) => (
  <Caption type="secondary" strong {...props} />
);

export const Meta = (props: CaptionProps) => (
  <Caption type="secondary" {...props} />
);

export const DataValue = ({ style, ...props }: TextProps) => (
  <Typography.Title
    level={2}
    style={{
      fontVariantNumeric: 'tabular-nums',
      ...style,
    }}
    {...props}
  />
);
